import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Activity, HeartHandshake, MessageCircle, Moon } from 'lucide-react';

const API_URL = window.location.origin;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const CHANNEL_LABELS = {
  reflection: 'Reflections', wins: 'Wins', wisdom: 'Wisdom', witness: 'Witness Wall',
  witness_circles: 'Witness Circles', lab: 'Legacy Lab', prayer: 'Prayer'
};

export const EngagementAdmin = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/legacy/engagement?days=${days}`, authHeaders())
      .then(res => setData(res.data))
      .catch(err => console.error('Failed to load engagement:', err));
  }, [days]);

  if (!data) return <Card className="card-base"><CardContent className="p-6"><div className="h-24 bg-sand rounded-xl animate-pulse" /></CardContent></Card>;

  const t = data.totals;

  return (
    <Card className="card-base" data-testid="admin-legacy-engagement">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
          <Activity className="h-5 w-5 text-terracotta" /> Circle Pulse
        </CardTitle>
        <div className="flex gap-1">
          {[14, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`text-xs px-3 py-1 rounded-full border ${days === d ? 'bg-forest text-white border-forest' : 'border-sand text-gray-500'}`}
              data-testid={`engagement-window-${d}`}>
              {d}d
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-gray-500 -mt-2">A care lens, not a scoreboard — see how the circle is breathing.</p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            ['Members engaged', `${t.active_members} of ${t.member_count}`],
            ['Posts shared', t.posts],
            ['Comments', t.comments],
            ['Journeys in motion', t.projects_active]
          ].map(([label, val]) => (
            <div key={label} className="bg-sand/50 rounded-xl p-3 text-center">
              <p className="font-heading text-xl font-bold text-forest">{val}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          ))}
        </div>

        {Object.keys(t.by_channel || {}).length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Where the conversation is living</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(t.by_channel).sort((a, b) => b[1] - a[1]).map(([ch, count]) => (
                <Badge key={ch} variant="outline" className="text-xs" data-testid={`engagement-channel-${ch}`}>
                  {CHANNEL_LABELS[ch] || ch}: {count}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
          <span className="flex items-center gap-1"><HeartHandshake className="h-4 w-4 text-terracotta" /> Prayers: {t.prayers_open} held · {t.prayers_answered} answered</span>
          <span className="flex items-center gap-1"><MessageCircle className="h-4 w-4 text-terracotta" /> Resources: {t.resources}</span>
          <span>Upcoming events: {t.upcoming_events}</span>
        </div>

        {data.quiet_members.length > 0 && (
          <div className="border border-champagne rounded-xl p-4 bg-champagne/10">
            <p className="text-sm font-medium text-forest flex items-center gap-2 mb-2">
              <Moon className="h-4 w-4 text-terracotta" /> Sisters to check in on ({data.quiet_members.length})
            </p>
            <p className="text-xs text-gray-500 mb-3">Quiet in the circle for the last {data.window_days} days. A personal note goes further than a nudge.</p>
            <div className="flex flex-wrap gap-2">
              {data.quiet_members.map(m => (
                <div key={m.id} className="flex items-center gap-2 bg-white rounded-full pl-1 pr-3 py-1 border border-sand" data-testid={`quiet-member-${m.id}`}>
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={m.profile_image || undefined} />
                    <AvatarFallback className="bg-forest text-white text-xs">{m.full_name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <span className="text-xs text-gray-700">{m.full_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EngagementAdmin;
